import { z } from 'zod';
import { DAY_KEY_RE, isValidDayKey } from '../funnel/dates';

/**
 * Texto opcional de una edición parcial (PATCH): si el campo no viene queda `undefined` (no se toca);
 * si viene vacío o null se guarda `null` (se borra). Nunca convertir «no enviado» en «borrar».
 */
export const optionalPatchText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .nullable()
    .transform((v) => (v === undefined ? undefined : v && v.length ? v : null));

export const cursorPaginationSchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type CursorPagination = z.infer<typeof cursorPaginationSchema>;

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
  /** Total con los mismos filtros (solo en la primera página, sin cursor). */
  total?: number;
}

/** Mes 'YYYY-MM' con mes válido (01–12). */
export const yearMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Mes inválido');

export const apiErrorSchema = z.object({
  statusCode: z.number(),
  error: z.string(),
  message: z.string(),
  details: z.unknown().optional(),
});
export type ApiError = z.infer<typeof apiErrorSchema>;

/**
 * Límite de un filtro «desde / hasta»: una fecha pura 'YYYY-MM-DD' (la web la manda tal como la eligió la persona
 * y la API la resuelve como día completo en la zona de la empresa con `dayBounds`) o un instante ISO **con hora y
 * zona** (`2026-09-30T04:00:00.000Z`). Nada más: `2026-02-30`, `2026-9-1` o `09/01/2026` responden 400 en vez de
 * colarse como medianoche UTC (que es lo que haría `z.coerce.date()`).
 */
export const dateBoundSchema = z.union([
  z
    .string()
    .regex(DAY_KEY_RE, 'Fecha en formato AAAA-MM-DD')
    .refine(isValidDayKey, 'Fecha inválida'),
  z
    .string()
    .datetime({ offset: true, message: 'Fecha y hora en formato ISO con zona' })
    .transform((s) => new Date(s)),
  z.date(),
]);
